import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { friendsAPI } from '../services/api';

export interface FriendRequestNotification {
  id: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    profilePicture?: string;
  };
  createdAt: string;
}

export interface GameInviteNotification {
  id: string;
  gameId: string;
  gameName: string;
  gameVariant: string;
  from: {
    id: string;
    firstName: string;
    lastName: string;
  };
  invitedAt: string;
}

interface NotificationsContextType {
  friendRequests: FriendRequestNotification[];
  gameInvites: GameInviteNotification[];
  totalCount: number;
  refresh: () => void;
  acceptFriend: (userId: string) => Promise<void>;
  rejectFriend: (userId: string) => Promise<void>;
  dismissInvite: (inviteId: string) => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const useNotifications = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return ctx;
};

export const NotificationsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [friendRequests, setFriendRequests] = useState<FriendRequestNotification[]>([]);
  const [gameInvites, setGameInvites] = useState<GameInviteNotification[]>([]);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await friendsAPI.getReceivedRequests();
      setFriendRequests(res.data || []);
    } catch (error) {
      console.error('Failed to load friend requests:', error);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setFriendRequests([]);
      setGameInvites([]);
      return;
    }

    refresh();

    const token = localStorage.getItem('token');
    if (!token) return;

    const socket: Socket = io('/', { auth: { token } });

    socket.on('connect', () => {
      if (user?.id) socket.emit('joinUserRoom', { userId: user.id });
    });

    socket.on('friendRequestReceived', () => refresh());
    socket.on('friendRequestAccepted', () => refresh());
    socket.on('friendRequestRejected', () => refresh());

    socket.on('gameInvitation', (data: Omit<GameInviteNotification, 'id'>) => {
      setGameInvites(prev => {
        // Avoid duplicate invites for the same game from the same person
        const exists = prev.some(
          inv => inv.gameId === data.gameId && inv.from?.id === data.from?.id
        );
        if (exists) return prev;
        return [{ ...data, id: `${data.gameId}-${data.from?.id}-${Date.now()}` }, ...prev];
      });
    });

    return () => {
      socket.disconnect();
    };
  }, [isAuthenticated, user?.id, refresh]);

  const acceptFriend = useCallback(async (userId: string) => {
    await friendsAPI.acceptRequest(userId);
    await refresh();
  }, [refresh]);

  const rejectFriend = useCallback(async (userId: string) => {
    await friendsAPI.rejectRequest(userId);
    await refresh();
  }, [refresh]);

  const dismissInvite = useCallback((inviteId: string) => {
    setGameInvites(prev => prev.filter(inv => inv.id !== inviteId));
  }, []);

  const totalCount = friendRequests.length + gameInvites.length;

  return (
    <NotificationsContext.Provider
      value={{
        friendRequests,
        gameInvites,
        totalCount,
        refresh,
        acceptFriend,
        rejectFriend,
        dismissInvite,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
